import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import axios from 'axios'
import './QuestChat.css'
import QuestWaking from './QuestWaking'
import QuestThinking from './QuestThinking'
import QuestSplash from './QuestSplash'
import { createQuestRealtimeSession } from '../lib/questRealtimeClient'

const REALTIME_VOICES = [
  { id: 'sage', name: 'Sage', description: 'Warm and conversational' },
  { id: 'marin', name: 'Marin', description: 'Bright and expressive' },
  { id: 'cedar', name: 'Cedar', description: 'Grounded and steady' },
  { id: 'coral', name: 'Coral', description: 'Clear and upbeat' },
  { id: 'ballad', name: 'Ballad', description: 'Smooth and measured' },
  { id: 'ash', name: 'Ash', description: 'Calm and direct' },
  { id: 'echo', name: 'Echo', description: 'Crisp and confident' },
  { id: 'shimmer', name: 'Shimmer', description: 'Light and friendly' },
  { id: 'alloy', name: 'Alloy', description: 'Balanced and neutral' },
  { id: 'verse', name: 'Verse', description: 'Energetic and natural' }
]

const REALTIME_VOICE_IDS = new Set(REALTIME_VOICES.map(voice => voice.id))
const DEFAULT_REALTIME_VOICE = 'sage'

function getSavedRealtimeVoice() {
  const savedVoice = localStorage.getItem('quest-voice')
  return REALTIME_VOICE_IDS.has(savedVoice) ? savedVoice : DEFAULT_REALTIME_VOICE
}

export default function QuestChat({ gameId, gameName, user, onDictation }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isDebugMode = searchParams.has('debug')
  const userDisplayName = user?.display_name || user?.username || 'there'
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [greeting, setGreeting] = useState('')
  const [greetingContext, setGreetingContext] = useState(null)
  const [showContext, setShowContext] = useState(false)
  const [loadingGreeting, setLoadingGreeting] = useState(false)
  const [error, setError] = useState(null) // { type: 'server'|'network'|'generic', retry: fn }
  const messagesEndRef = useRef(null)
  const realtimeClientRef = useRef(null)
  const realtimePartialMessageIdRef = useRef(null)
  const realtimeVoiceUserMessageRef = useRef(null)
  const realtimeVoiceUserMessageIdRef = useRef(null)
  const realtimeVoiceUserMessageQueueRef = useRef([])

  const [selectedVoice, setSelectedVoice] = useState(() => {
    return getSavedRealtimeVoice()
  })
  const [showVoiceMenu, setShowVoiceMenu] = useState(false)
  const [previewingVoice, setPreviewingVoice] = useState(null)
  const voicePreviewAudioRef = useRef(null)

  // Realtime voice state
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [voiceTranscriptDraft, setVoiceTranscriptDraft] = useState('')

  // Quest Ritual removed - go straight to chat

  useEffect(() => {
    // Clear greeting and load new history when game changes
    setGreeting('')
    setGreetingContext(null)
    if (realtimeClientRef.current) {
      realtimeClientRef.current.close()
      realtimeClientRef.current = null
    }
    realtimeVoiceUserMessageRef.current = null
    realtimeVoiceUserMessageIdRef.current = null
    realtimeVoiceUserMessageQueueRef.current = []
    realtimePartialMessageIdRef.current = null
    setIsRecording(false)
    setIsTranscribing(false)
    setVoiceTranscriptDraft('')
    loadHistory()
  }, [gameId])

  useEffect(() => {
    return () => {
      if (realtimeClientRef.current) {
        realtimeClientRef.current.close()
        realtimeClientRef.current = null
      }
      if (voicePreviewAudioRef.current) {
        voicePreviewAudioRef.current.pause()
        voicePreviewAudioRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    // Fetch personalized greeting when there are no messages
    if (messages.length === 0 && !loadingGreeting && !greeting) {
      loadGreeting()
    }
  }, [messages, loadingGreeting, greeting, gameId])

  const scrollToBottom = () => {
    const messagesContainer = messagesEndRef.current?.parentElement
    if (!messagesContainer) return

    // Use requestAnimationFrame to ensure DOM has updated and measurements are accurate
    requestAnimationFrame(() => {
      // Get the last message element
      const messageElements = messagesContainer.querySelectorAll('.message')
      if (messageElements.length === 0) return

      const lastMessage = messageElements[messageElements.length - 1]
      const containerHeight = messagesContainer.clientHeight
      const lastMessageHeight = lastMessage.offsetHeight

      // Calculate max scrollable position
      const maxScroll = messagesContainer.scrollHeight - messagesContainer.clientHeight

      // If the last message is taller than the viewport, scroll to show its top
      // Otherwise, scroll to the bottom to show the complete message
      if (lastMessageHeight > containerHeight * 0.8) {
        // Message is tall - scroll to show the beginning using scrollIntoView
        // Use double RAF to ensure this runs after any other scroll events
        requestAnimationFrame(() => {
          lastMessage.scrollIntoView({ behavior: 'instant', block: 'start' })
        })
      } else {
        // Message fits - scroll to bottom to show it completely
        messagesContainer.scrollTop = maxScroll
      }
    })
  }

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const loadHistory = async () => {
    try {
      const response = await axios.get(`/api/quest/history?game_id=${gameId}`, {
        withCredentials: true
      })
      setMessages(response.data)
    } catch (error) {
      console.error('Failed to load chat history:', error)
    }
  }

  const loadGreeting = async () => {
    setLoadingGreeting(true)
    try {
      // Get last viewed game from sessionStorage
      const lastViewedGameId = sessionStorage.getItem('lastViewedGame')

      const params = {
        game_id: gameId,
        game_name: gameName
      }

      // Include last viewed game if available
      if (lastViewedGameId && lastViewedGameId !== gameId) {
        params.last_viewed_game_id = lastViewedGameId
      }

      const response = await axios.get('/api/quest/greeting', {
        params,
        withCredentials: true
      })

      setGreeting(response.data.greeting)
      setGreetingContext(response.data.context)
    } catch (error) {
      console.error('Failed to load greeting:', error)
      setGreeting(`Yo ${userDisplayName}! Want to chat about ${gameName}?`)
    } finally {
      setLoadingGreeting(false)
    }
  }

  const ensureRealtimePartialMessage = () => {
    if (realtimePartialMessageIdRef.current) {
      return realtimePartialMessageIdRef.current
    }

    if (isRecording && !realtimeVoiceUserMessageIdRef.current) {
      ensureRealtimeVoiceUserMessage('', false)
    }

    const messageId = `realtime-voice-${Date.now()}`
    realtimePartialMessageIdRef.current = messageId
    setMessages(prev => [...prev, {
      message_id: messageId,
      message_role: 'quest',
      message_text: '',
      created_at: new Date().toISOString()
    }])
    return messageId
  }

  const ensureRealtimeVoiceUserMessage = (text, isFinal) => {
    if (!realtimeVoiceUserMessageIdRef.current) {
      const messageId = `realtime-user-${Date.now()}`
      realtimeVoiceUserMessageIdRef.current = messageId
      setMessages(prev => [...prev, {
        message_id: messageId,
        message_role: 'user',
        message_text: text || '...',
        created_at: new Date().toISOString(),
        is_transcript_pending: !isFinal
      }])
      return messageId
    }

    const messageId = realtimeVoiceUserMessageIdRef.current
    setMessages(prev => prev.map(msg =>
      msg.message_id === messageId
        ? {
            ...msg,
            message_text: text || msg.message_text,
            is_transcript_pending: !isFinal
          }
        : msg
    ))
    return messageId
  }

  // Start GPT Realtime voice capture.
  const startVoiceSession = async () => {
    if (isRecording || loading) return
    setIsTranscribing(true)
    setVoiceTranscriptDraft('')

    try {
      const client = await ensureRealtimeClient({ forceVoice: true })
      client.setMicrophoneEnabled(true)
      setIsRecording(true)
    } catch (err) {
      console.error('Failed to start Realtime voice:', err)
      alert('Could not access microphone. Please allow microphone access.')
    } finally {
      setIsTranscribing(false)
    }
  }

  // Stop Realtime voice capture and close the session so the next click starts cleanly.
  const stopVoiceSession = () => {
    if (realtimeClientRef.current) {
      realtimeClientRef.current.close()
      realtimeClientRef.current = null
    }
    realtimeVoiceUserMessageRef.current = null
    realtimeVoiceUserMessageIdRef.current = null
    realtimeVoiceUserMessageQueueRef.current = []
    realtimePartialMessageIdRef.current = null
    setIsRecording(false)
    setIsTranscribing(false)
    setVoiceTranscriptDraft('')
  }

  const toggleVoiceSession = async () => {
    if (isRecording) {
      stopVoiceSession()
    } else {
      await startVoiceSession()
    }
  }

  const ensureRealtimeClient = async ({ forceVoice = false } = {}) => {
    if (realtimeClientRef.current) {
      if (forceVoice && !realtimeClientRef.current.voiceEnabled) {
        realtimeClientRef.current.close()
        realtimeClientRef.current = null
      } else {
        return realtimeClientRef.current
      }
    }

    if (forceVoice) {
      realtimeVoiceUserMessageRef.current = null
      realtimeVoiceUserMessageIdRef.current = null
      realtimeVoiceUserMessageQueueRef.current = []
    }

    if (realtimeClientRef.current) {
      return realtimeClientRef.current
    }

    const client = await createQuestRealtimeSession({
      gameId,
      gameName,
      voiceEnabled: forceVoice,
      selectedVoice,
      onTextDelta: (text) => {
        const messageId = ensureRealtimePartialMessage()
        setLoading(false)
        setMessages(prev => prev.map(msg =>
          msg.message_id === messageId
            ? { ...msg, message_text: text }
            : msg
        ))
      },
      onFinalText: (text) => {
        const messageId = ensureRealtimePartialMessage()
        setMessages(prev => prev.map(msg =>
          msg.message_id === messageId
            ? { ...msg, message_text: text }
            : msg
        ))

        const userMessage = realtimeVoiceUserMessageQueueRef.current.shift() || realtimeVoiceUserMessageRef.current
        if (userMessage) {
          persistRealtimeTurn(userMessage, text).catch(err => {
            console.error('Failed to persist Realtime voice turn:', err)
          })
          realtimeVoiceUserMessageRef.current = null
          realtimePartialMessageIdRef.current = null
        }
      },
      onUserTranscript: (text, isFinal) => {
        if (!isFinal && text) {
          setVoiceTranscriptDraft(prev => {
            const nextDraft = `${prev}${text}`
            ensureRealtimeVoiceUserMessage(nextDraft, false)
            return nextDraft
          })
          return
        }

        if (isFinal) {
          setVoiceTranscriptDraft('')
        }

        if (isFinal && text?.trim()) {
          const transcript = text.trim()
          realtimeVoiceUserMessageRef.current = transcript
          realtimeVoiceUserMessageQueueRef.current.push(transcript)
          ensureRealtimeVoiceUserMessage(transcript, true)
          realtimeVoiceUserMessageIdRef.current = null
          onDictation?.(transcript)
        }
      },
      onError: (err) => {
        console.error('Quest Realtime error:', err)
      }
    })

    realtimeClientRef.current = client
    return client
  }

  const persistRealtimeTurn = async (userMessage, questResponse) => {
    await axios.post(
      '/api/quest/realtime/turns',
      {
        user_message: userMessage,
        quest_response: questResponse,
        game_id: gameId,
        game_name: gameName
      },
      { withCredentials: true }
    )
  }

  const sendMessage = async (e) => {
    e.preventDefault()
    if (!input.trim()) return

    const userMessage = input.trim()
    setInput('')
    setLoading(true)

    // Add user message immediately
    setMessages(prev => [...prev, {
      message_role: 'user',
      message_text: userMessage,
      created_at: new Date().toISOString()
    }])

    const tempMessageId = `realtime-${Date.now()}`
    realtimePartialMessageIdRef.current = tempMessageId

    try {
      setMessages(prev => [...prev, {
        message_id: tempMessageId,
        message_role: 'quest',
        message_text: '',
        created_at: new Date().toISOString()
      }])

      const realtimeClient = await ensureRealtimeClient()
      const questResponse = await realtimeClient.sendText(userMessage)

      if (!questResponse) {
        setMessages(prev => prev.filter(msg => msg.message_id !== tempMessageId))
        return
      }

      await persistRealtimeTurn(userMessage, questResponse)
    } catch (err) {
      console.error('Realtime Quest path failed:', err)
      setMessages(prev => prev.filter(msg => msg.message_id !== tempMessageId))

      let errorType = 'generic'
      if (err.response) {
        if (err.response.status === 504 || err.response.status === 408) {
          errorType = 'network'
        } else if (err.response.status === 429) {
          errorType = 'rate'
        } else if (err.response.status >= 500) {
          errorType = 'server'
        }
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorType = 'network'
      }

      setError({
        type: errorType,
        retry: () => {
          setError(null)
          setInput(userMessage)
        }
      })
    } finally {
      setLoading(false)
      realtimePartialMessageIdRef.current = null
    }
  }

  const clearConversation = async () => {
    if (!window.confirm('Clear this conversation with Quest? This cannot be undone!')) {
      return
    }

    try {
      await axios.delete('/api/quest/clear', {
        data: { game_id: gameId, game_name: gameName },
        withCredentials: true
      })
      setMessages([])
    } catch (error) {
      console.error('Failed to clear conversation:', error)
      alert('Failed to clear conversation. Please try again.')
    }
  }

  const parseMarkdownAndLinks = (text) => {
    // Parse both markdown (**bold**) and [game links]
    const parts = []
    const regex = /(\*\*([^*]+)\*\*|\[([^\]]+)\])/g
    let lastIndex = 0
    let match

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        })
      }

      // Check if it's bold text or game link
      if (match[2]) {
        // Bold text: **text**
        parts.push({
          type: 'bold',
          content: match[2]
        })
      } else if (match[3]) {
        // Game link: [game name]
        parts.push({
          type: 'game',
          content: match[3]
        })
      }

      lastIndex = regex.lastIndex
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      })
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }]
  }

  // Handle voice selection
  const handleVoiceSelect = (voiceId) => {
    if (!REALTIME_VOICE_IDS.has(voiceId)) return

    if (realtimeClientRef.current && voiceId !== selectedVoice) {
      realtimeClientRef.current.close()
      realtimeClientRef.current = null
      realtimeVoiceUserMessageRef.current = null
      realtimeVoiceUserMessageIdRef.current = null
      realtimeVoiceUserMessageQueueRef.current = []
      realtimePartialMessageIdRef.current = null
      setIsRecording(false)
      setIsTranscribing(false)
      setVoiceTranscriptDraft('')
    }

    setSelectedVoice(voiceId)
    localStorage.setItem('quest-voice', voiceId)
    playVoicePreview(voiceId)
  }

  const playVoicePreview = async (voiceId) => {
    if (!REALTIME_VOICE_IDS.has(voiceId)) return

    if (voicePreviewAudioRef.current) {
      voicePreviewAudioRef.current.pause()
      voicePreviewAudioRef.current = null
    }

    setPreviewingVoice(voiceId)

    try {
      const response = await axios.post(
        '/api/quest/realtime/voice-preview',
        { voice: voiceId },
        {
          withCredentials: true,
          responseType: 'blob'
        }
      )
      const audioUrl = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }))
      const audio = new Audio(audioUrl)
      voicePreviewAudioRef.current = audio
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl)
        if (voicePreviewAudioRef.current === audio) {
          voicePreviewAudioRef.current = null
        }
        setPreviewingVoice(null)
      }
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl)
        if (voicePreviewAudioRef.current === audio) {
          voicePreviewAudioRef.current = null
        }
        setPreviewingVoice(null)
      }
      await audio.play()
    } catch (err) {
      console.error('Failed to preview Realtime voice:', err)
      setPreviewingVoice(null)
    }
  }

  const handleGameClick = async (gameName) => {
    try {
      // Search for the game using RAWG API
      const response = await axios.get('/api/games/search', {
        params: { q: gameName, page_size: 1 },
        withCredentials: true
      })

      const results = response.data.results
      if (results && results.length > 0) {
        // Navigate directly to the first game result
        const gameId = results[0].id
        navigate(`/game/${gameId}`)
      } else {
        // Fallback: Navigate to search page if no results found
        navigate(`/?search=${encodeURIComponent(gameName)}`)
      }
    } catch (error) {
      console.error('Failed to search for game:', error)
      // Fallback: Navigate to search page on error
      navigate(`/?search=${encodeURIComponent(gameName)}`)
    }
  }

  return (
    <div className="quest-chat">
      <div className="quest-chat-header">
        <motion.div
          className="quest-avatar"
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          🗺️
        </motion.div>
        <div className="quest-info">
          <div className="quest-name">Quest</div>
          <div className="quest-status">Your gaming buddy</div>
        </div>
        <div className="voice-controls">
          <motion.button
            className="voice-menu-btn"
            onClick={() => setShowVoiceMenu(!showVoiceMenu)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Change voice"
            title="Change voice"
          >
            🎭
          </motion.button>
          <AnimatePresence>
            {showVoiceMenu && (
              <motion.div
                className="voice-menu"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="voice-menu-title">Quest's Voice</div>
                <div className="voice-category-label">Realtime voices</div>
                {REALTIME_VOICES.map(voice => (
                  <button
                    key={voice.id}
                    className={`voice-option ${selectedVoice === voice.id ? 'selected' : ''}`}
                    onClick={() => handleVoiceSelect(voice.id)}
                  >
                    <span className="voice-name">{voice.name}</span>
                    <span className="voice-desc">
                      {previewingVoice === voice.id ? 'Playing sample...' : voice.description}
                    </span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {messages.length > 0 && (
          <motion.button
            className="clear-chat-btn"
            onClick={clearConversation}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Clear conversation"
          >
            🗑️
          </motion.button>
        )}
      </div>

      <div className="quest-messages">
        <AnimatePresence initial={false}>
          {messages.length === 0 && (
            <motion.div
              className="quest-welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="welcome-icon">
                {loadingGreeting ? (
                  <QuestWaking isLoading={loadingGreeting} />
                ) : (
                  '👋'
                )}
              </div>
              <p>
                {loadingGreeting ? (
                  'Quest is thinking...'
                ) : (
                  parseMarkdownAndLinks(greeting || `Yo ${userDisplayName}! Want to chat about ${gameName}?`).map((part, i) => (
                    part.type === 'game' ? (
                      <span
                        key={i}
                        className="game-link"
                        onClick={() => handleGameClick(part.content)}
                      >
                        {part.content}
                      </span>
                    ) : part.type === 'bold' ? (
                      <strong key={i}>{part.content}</strong>
                    ) : (
                      <span key={i}>{part.content}</span>
                    )
                  ))
                )}
              </p>
              {isDebugMode && greetingContext && (
                <div className="greeting-context-section">
                  <button
                    className="context-toggle-btn"
                    onClick={() => setShowContext(!showContext)}
                  >
                    {showContext ? '🔽' : '🔼'} Quest's Context
                  </button>
                  {showContext && (
                    <div className="greeting-context">
                      <div><strong>Current game:</strong> {greetingContext.current_game}</div>
                      {greetingContext.last_viewed_game && (
                        <div><strong>Last viewed:</strong> {greetingContext.last_viewed_game}</div>
                      )}
                      <div><strong>Total chat history:</strong> {greetingContext.history_size} messages</div>

                      {greetingContext.recent_messages && greetingContext.recent_messages.length > 0 && (
                        <div className="recent-messages-list">
                          <strong>Recent messages (last {greetingContext.recent_messages.length}):</strong>
                          <div className="messages-scroll">
                            {greetingContext.recent_messages.map((msg, i) => (
                              <div key={i} className="context-message">
                                <span className="message-game">[{msg.game || 'General'}]</span>
                                <span className="message-role">{msg.role === 'user' ? userDisplayName : 'Quest'}:</span>
                                <span className="message-text">{msg.text}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {messages.map((msg, index) => (
            <motion.div
              key={index}
              className={`message ${msg.message_role}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="message-bubble">
                {msg.message_role === 'quest' ? (
                  parseMarkdownAndLinks(msg.message_text).map((part, i) => (
                    part.type === 'game' ? (
                      <span
                        key={i}
                        className="game-link"
                        onClick={() => handleGameClick(part.content)}
                      >
                        {part.content}
                      </span>
                    ) : part.type === 'bold' ? (
                      <strong key={i}>{part.content}</strong>
                    ) : (
                      <span key={i}>{part.content}</span>
                    )
                  ))
                ) : (
                  msg.message_text
                )}
              </div>
            </motion.div>
          ))}

          {loading && (
            <motion.div
              className="message quest"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <QuestThinking isThinking={loading} />
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Error splash - SPLASH not BUZZER */}
      <AnimatePresence>
        {error && (
          <QuestSplash
            isVisible={!!error}
            errorType={error.type}
            onRetry={error.retry}
            onDismiss={() => setError(null)}
          />
        )}
      </AnimatePresence>

      <div className="quest-input-area">
        {(isRecording || voiceTranscriptDraft) && (
          <div className={`quest-voice-transcript-draft ${voiceTranscriptDraft ? 'has-text' : ''}`}>
            {voiceTranscriptDraft || 'Listening for your voice...'}
          </div>
        )}
        <form className="quest-input-form" onSubmit={sendMessage}>
          <motion.button
            type="button"
            className={`quest-mic-btn ${isRecording ? 'recording' : ''} ${isTranscribing ? 'transcribing' : ''}`}
            onClick={toggleVoiceSession}
            disabled={loading || isTranscribing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={isRecording ? 'Stop Quest voice' : 'Start Quest voice'}
          >
            {isTranscribing ? '⏳' : isRecording ? '🔴' : '🎤'}
          </motion.button>
          <input
            type="text"
            className="quest-input"
            placeholder={isTranscribing ? 'Starting voice...' : isRecording ? 'Quest is listening...' : 'Type or start voice...'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading || isTranscribing}
          />
          <motion.button
            type="submit"
            className="quest-send-btn"
            disabled={loading || !input.trim() || isTranscribing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Send
          </motion.button>
        </form>
      </div>
    </div>
  )
}
